import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, CardBody, Row, Col, Button, Badge, Form, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import { format } from 'date-fns';
import imageCompression from 'browser-image-compression';
import axios from 'axios';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TeamsTable from '../../Common/TeamsTable';
import { getTournamentTeamRegistrationById } from '../../api/tournament/registeredTournamentsService';
import { getAllClubs, ClubListingData } from '../../api/club/allClubsService';
import { createTeam, CreateTeamRequest, deleteTeam, getTeamById, updateTeam } from '../../api/club/teamService';
import { getPaymentPreview } from '../../api/tournament/paymentService';
import { getPlayersByTeamId } from '../../api/club/playerService';
import { exportTournamentTeams } from '../../api/clubmanager/tournamentService';
import { Team } from '../../types/Team';
import { DeleteModal } from '../../Common/DeleteModal';
import secureApi from '../../utils/secureApi';
import './RegisteredTournamentDetail.css';
import { getImageUrl } from '../../utils/imageUrl';

interface TournamentTeamRegistration {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  sports: string;
  ageCategories: string[];
  teamCount?: string;
  verifiedCount?: string;
}

const RegisteredTournamentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [tournament, setTournament] = useState<TournamentTeamRegistration | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [totalTeams, setTotalTeams] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTournament, setEditedTournament] = useState<TournamentTeamRegistration | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isTournamentDetailsExpanded, setIsTournamentDetailsExpanded] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isRecordPaymentLoading, setIsRecordPaymentLoading] = useState(false);
  const [recordPaymentLoadingTeamId, setRecordPaymentLoadingTeamId] = useState<string | null>(null);
  const [recordPaymentError, setRecordPaymentError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Team details loading state
  const [isLoadingTeamDetails, setIsLoadingTeamDetails] = useState(false);
  const [teamDetailsError, setTeamDetailsError] = useState<string | null>(null);

  // Ref for scrolling to team form
  const teamFormRef = useRef<HTMLDivElement>(null);
  
  // Add refs for request management
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  


  // React Select options
  const sportsOptions = [
    { value: 'Football', label: 'Football' }
  ];

  // Complete age category options list (same as Manage Tournaments)
  const ageCategories = [
    'U5', 'U6', 'U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 
    'U15', 'U16', 'U17', 'U18', 'Sub Junior Boys', 'Junior Boys', 
    'Elite Boys', 'U14 Girls', 'U17 Girls'
  ];

  // Age Category options - structured like main listing for uniformity
  const ageCategoryOptions = tournament?.ageCategories ? 
    [
      { value: 'all', label: 'All' },
      ...tournament.ageCategories.map(category => ({ value: category, label: category }))
    ] : [
      { value: 'all', label: 'All' }
    ];

  // Mock teams data - replace with actual API call
  const mockTeams: Team[] = [
    {
      _id: '1',
      name: 'Kandivali FC',
      shortName: 'KFC',
      logo: '/path/to/logo1.png',
      ageGroups: ['U11'],
      coachManagerName: 'John Doe',
      contactDetails: '9876543210',
      players: 11,
      verifiedPlayers: 10,
      paidPlayers: 10,
      registration: null // No registration - will show Add Player, Manage Players, and Record Payment
    },
    {
      _id: '2',
      name: 'Oranje FC',
      shortName: 'OFC',
      logo: '/path/to/logo2.png',
      ageGroups: ['U11'],
      coachManagerName: 'Jane Smith',
      contactDetails: '9876543211',
      players: 11,
      verifiedPlayers: 10,
      paidPlayers: 9
    }
  ];

  const loadTeams = useCallback(async (page?: number, search?: string, ageFilter?: string, forceLoad = false) => {
    console.log('📞 loadTeams function called with:', { page, search, ageFilter, id, forceLoad });
    
    // Prevent multiple simultaneous calls
    if (teamsLoading && !forceLoad) {
      console.log('⏸️ Teams already loading, skipping duplicate call');
      return;
    }
    
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      console.log('🚫 Cancelling previous request');
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    try {
      setTeamsLoading(true);
      setIsPageLoading(true);
      
      // Get tournament ID from URL params - this is the key fix!
      const tournamentId = id; // Use the actual tournament ID from the URL
      
      if (!tournamentId) {
        console.error('❌ No tournament ID found in URL');
        setTeams([]);
        setTotalTeams(0);
        setTotalPages(0);
        return;
      }
      
      // Use provided parameters or fall back to current state
      const pageToLoad = page ?? currentPage;
      const searchToUse = search ?? searchTerm;
      const ageFilterToUse = ageFilter ?? ageGroupFilter;
      
      // Build the API URL with the correct tournament ID
      const apiUrl = `/api/v1/club/list-all-teams-for-registration/${tournamentId}`;
      const params: any = {
        pageNum: pageToLoad
      };
      
      // Add optional filters if they have values
      if (searchToUse && searchToUse.trim()) {
        params.search = searchToUse.trim();
      }
      if (ageFilterToUse && ageFilterToUse !== 'all') {
        params.ageCategory = ageFilterToUse;
      }
      
      console.log('🆔 Tournament ID from URL:', tournamentId);
      console.log('🌐 API URL:', apiUrl);
      console.log('📋 Request params:', params);
      console.log('🔗 Full URL:', `${process.env.REACT_APP_API_BASE_URL}${apiUrl}?${new URLSearchParams(params).toString()}`);
      console.log('🚀 Making API call...');
      console.log('⏱️ API call started at:', new Date().toISOString());
      
      // Make the API call with abort signal
      const response = await secureApi.get(apiUrl, { 
        params,
        signal: abortControllerRef.current.signal 
      });
      
      console.log('🎉 API call completed at:', new Date().toISOString());
      console.log('📡 API Response:', response);
      
      // Parse the response according to the provided structure
      if ((response.status === 200 || response.status === 304) && response.data?.statusCode === 200) {
        const teamsData = response.data.data?.paginatedResults || [];
        const totalCount = response.data.data?.totalCount || 0;
        const totalPages = response.data.data?.totalPages || Math.ceil(totalCount / 10);
        
        console.log('🔍 Raw team data sample:', teamsData[0]); // Log first team for debugging
        console.log('🔍 Complete team object keys:', teamsData[0] ? Object.keys(teamsData[0]) : 'No teams');
        console.log('🔍 Team clubId field:', teamsData[0]?.clubId);
        console.log('🔍 Team club field:', teamsData[0]?.club);
        
        // Map the API response to our Team interface
        const formattedTeams: Team[] = teamsData.map((team: any) => {
          // Enhanced club ID extraction with multiple fallbacks
          const clubId = team.clubId || 
                        team.club?.id || 
                        team.clubId || 
                        team.club_id || 
                        team.clubId || 
                        '';
          
          console.log(`🔍 Team "${team.name}" club mapping:`, {
            originalClubId: team.clubId,
            clubObject: team.club,
            clubObjectId: team.club?.id,
            allClubFields: {
              clubId: team.clubId,
              club_id: team.club_id,
              club: team.club,
              clubName: team.clubName,
              clubNameId: team.clubNameId,
              associatedClub: team.associatedClub,
              parentClub: team.parentClub
            },
            finalClubId: clubId
          });
          
          // Debug: Log the raw team data from API
          console.log('🔍 Raw team data from API:', {
            teamId: team._id,
            teamName: team.name,
            playerIds: team.playerIds,
            paidPlayerIds: team.paidPlayerIds,
            playersCount: team.playersCount,
            paidPlayers: team.paidPlayers
          });
          
          // ✅ SIMPLIFIED PAYMENT AMOUNT CALCULATION - Use API values directly
          return {
            _id: team._id,
            name: team.name || "",
            shortName: team.shortName || "",
            logo: team.logo || "",
            ageGroups: team.ageCategory ? [team.ageCategory] : [],
            coachManagerName: team.coachManagerName || team.coachName || team.managerName || "",
            contactDetails: team.contactDetails || team.contactNumber || team.phone || "",
            players: team.playersCount || 0,
            verifiedPlayers: team.approvedPlayersCount || 0,
            paidPlayers: team.paidPlayerIds ? team.paidPlayerIds.length : 0,
            paidPlayerIds: team.paidPlayerIds || [],
            playerIds: team.playerIds || [],
            registration: team.registration || null,
            clubId: clubId,
            // ✅ SIMPLIFIED: Use API values directly without complex calculation
            totalAmount: team.totalAmount || 0,
            amountPaid: team.amountPaid || 0,
            balanceAmount: team.balanceAmount || 0
          };
        });
        
        console.log("🎯 Formatted teams:", formattedTeams);        setTeams(formattedTeams);
        setTotalTeams(totalCount);
        setTotalPages(totalPages);
        
      } else if (response.status === 304 && !response.data) {
        console.log('📋 API returned 304 Not Modified - data hasn\'t changed');
        // For 304, the response might not have a body, but we should check if there's cached data
        // Let's try to handle this by making a fresh request
        console.log('🔄 Making fresh request due to 304...');
        
        // Add a cache-busting parameter to force a fresh response
        const freshParams = { 
          ...params, 
          _t: Date.now() // Cache buster
        };
        
        try {
          const freshResponse = await secureApi.get(apiUrl, { 
            params: freshParams,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          console.log('🆕 Fresh response:', freshResponse);
          
          if (freshResponse.status === 200 && freshResponse.data?.statusCode === 200) {
            const teamsData = freshResponse.data.data?.paginatedResults || [];
            const totalCount = freshResponse.data.data?.totalCount || 0;
            const totalPages = freshResponse.data.data?.totalPages || Math.ceil(totalCount / 10);
            
            const formattedTeams: Team[] = teamsData.map((team: any) => {
              // Enhanced club ID extraction with multiple fallbacks
              const clubId = team.clubId || 
                            team.club?.id || 
                            team.clubId || 
                            team.club_id || 
                            team.clubId || 
                            '';
              
              // Debug: Log the raw team data from fresh API call
              console.log('🔍 Raw team data from fresh API call:', {
                teamId: team._id,
                teamName: team.name,
                playerIds: team.playerIds,
                paidPlayerIds: team.paidPlayerIds,
                playersCount: team.playersCount,
                paidPlayers: team.paidPlayers
              });
              
              return {
                _id: team._id,
                name: team.name || '',
                shortName: team.shortName || '',
                logo: team.logo || '',
                ageGroups: team.ageCategory ? [team.ageCategory] : [],
                coachManagerName: team.coachManagerName || team.coachName || team.managerName || '',
                contactDetails: team.contactDetails || team.contactNumber || team.phone || '',
                players: team.playersCount || 0,
                verifiedPlayers: team.approvedPlayersCount || 0,
                paidPlayers: team.paidPlayerIds ? team.paidPlayerIds.length : 0,
                paidPlayerIds: team.paidPlayerIds || [],
                playerIds: team.playerIds || [],
                registration: team.registration || null,
                clubId: clubId,
                // ✅ SIMPLIFIED: Use API values directly without complex calculation
                totalAmount: team.totalAmount || 0,
                amountPaid: team.amountPaid || 0,
                balanceAmount: team.balanceAmount || 0
              };
            });
            
            setTeams(formattedTeams);
            setTotalTeams(totalCount);
            setTotalPages(totalPages);
          } else {
            console.log('📭 Fresh request also returned no teams');
            setTeams([]);
            setTotalTeams(0);
            setTotalPages(0);
          }
        } catch (freshError) {
          console.log('❌ Fresh request failed, keeping current state');
          // Don't change the current state on fresh request failure
        }
        
      } else if (response.status === 204) {
        console.log('📭 API returned 204 - No teams found');
        setTeams([]);
        setTotalTeams(0);
        setTotalPages(0);
      } else {
        console.log('⚠️ Unexpected response structure:', {
          status: response.status,
          statusCode: response.data?.statusCode,
          data: response.data
        });
        
        // Try to extract teams data even with unexpected structure
        let teamsData = [];
        let totalCount = 0;
        
        if (response.data?.data?.paginatedResults) {
          teamsData = response.data.data.paginatedResults;
          totalCount = response.data.data.totalCount || 0;
        } else if (response.data?.paginatedResults) {
          teamsData = response.data.paginatedResults;
          totalCount = response.data.totalCount || 0;
        } else if (Array.isArray(response.data)) {
          teamsData = response.data;
          totalCount = response.data.length;
        }
        
        const totalPages = Math.ceil(totalCount / 10);
        
        if (teamsData.length > 0) {
          const formattedTeams: Team[] = teamsData.map((team: any) => {
            // Enhanced club ID extraction with multiple fallbacks
            const clubId = team.clubId || 
                          team.club?.id || 
                          team.clubId || 
                          team.club_id || 
                          team.clubId || 
                          '';
            
            return {
              _id: team._id,
              name: team.name || '',
              shortName: team.shortName || '',
              logo: team.logo || '',
              ageGroups: team.ageCategory ? [team.ageCategory] : [],
              coachManagerName: team.coachManagerName || team.coachName || team.managerName || '',
              contactDetails: team.contactDetails || team.contactNumber || team.phone || '',
              players: team.playersCount || 0,
              verifiedPlayers: team.approvedPlayersCount || 0,
              paidPlayers: team.paidPlayerIds ? team.paidPlayerIds.length : 0,
              registration: team.registration || null,
              clubId: clubId,
              // ✅ SIMPLIFIED: Use API values directly without complex calculation
              totalAmount: team.totalAmount || 0,
              amountPaid: team.amountPaid || 0,
              balanceAmount: team.balanceAmount || 0
            };
          });
          
          setTeams(formattedTeams);
          setTotalTeams(totalCount);
          setTotalPages(totalPages);
        } else {
          setTeams([]);
          setTotalTeams(0);
          setTotalPages(0);
        }
      }
      
    } catch (error: any) {
      // Don't log error if request was aborted
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
      console.error('❌ Error loading teams:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error message:', error.response?.statusText);
      
      // Show empty state on error
      setTeams([]);
      setTotalTeams(0);
      setTotalPages(0);
      } else {
        console.log('🚫 Request was cancelled');
      }
    } finally {
      // Only update loading state if this is the current request
      if (!abortControllerRef.current?.signal.aborted) {
      setTeamsLoading(false);
      setIsPageLoading(false);
      }
    }
  }, [id]);

  // Single useEffect for all data loading
  useEffect(() => {
    if (!id) return;
    
    console.log('🚀 Data loading useEffect triggered:', { 
      id, 
      searchTerm, 
      ageGroupFilter, 
      isInitial: isInitialLoadRef.current 
    });
    
    // Load tournament details on initial load
    if (isInitialLoadRef.current) {
      loadTournamentDetails();
      isInitialLoadRef.current = false;
      // Load teams immediately for initial load
      loadTeams(1, searchTerm, ageGroupFilter);
    } else {
      // Use debounced version for search/filter changes
      setCurrentPage(1); // Reset to first page when search/filter changes
      
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set new timer - call loadTeams directly to avoid dependency issues
      debounceTimerRef.current = setTimeout(() => {
      loadTeams(1, searchTerm, ageGroupFilter);
      }, 300);
    }
    
    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchTerm, ageGroupFilter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);



  const loadTournamentDetails = async () => {
    try {
              setLoading(true);
        if (id) {
          const tournamentData = await getTournamentTeamRegistrationById(id);
          setTournament(tournamentData);
        }
    } catch (error) {
      console.error('Error loading tournament details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleEditTournament = () => {
    // Navigate to tournament edit page
    navigate(`/manage-tournaments/${id}`);
  };

  const handleToggleEditMode = () => {
    if (!isEditMode && tournament) {
      setEditedTournament({ ...tournament });
      // Auto-expand the card when entering edit mode
      setIsTournamentDetailsExpanded(true);
    }
    setIsEditMode(!isEditMode);
    setHasChanges(false); // Reset hasChanges when toggling edit mode
  };

  const handleSaveTournament = async () => {
    try {
      if (!editedTournament || !id) return;
      
      // Use the same API as Manage Tournaments
      const response = await secureApi.post(
        `/api/v1/tournaments/manage/update-tournament-details/${id}`,
        editedTournament
      );
      
      setTournament(editedTournament);
      setIsEditMode(false);
      setHasChanges(false);
      setEditedTournament(null);
      // Keep the card expanded after saving so user can see the updated details
      setIsTournamentDetailsExpanded(true);
      
      console.log('Tournament updated successfully:', response);
    } catch (error) {
      console.error('Error saving tournament:', error);
      // You can add error handling here (show toast, alert, etc.)
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedTournament(null);
    setHasChanges(false);
    // Reset to collapsed state when canceling edit
    setIsTournamentDetailsExpanded(false);
  };

  const handleTournamentFieldChange = (field: keyof TournamentTeamRegistration, value: any) => {
    if (editedTournament) {
      const updated: any = {
        ...editedTournament,
        [field]: value
      };

      // Enforce date relationships
      if (field === 'startDate') {
        if (updated.endDate && new Date(updated.endDate) < new Date(value)) {
          updated.endDate = value;
        }
      }
      if (field === 'endDate') {
        if (updated.startDate && new Date(value) < new Date(updated.startDate)) {
          updated.endDate = updated.startDate;
        }
      }
      if (field === 'registrationStartDate') {
        if (updated.registrationEndDate && new Date(updated.registrationEndDate) < new Date(value)) {
          updated.registrationEndDate = value;
        }
      }
      if (field === 'registrationEndDate') {
        if (updated.registrationStartDate && new Date(value) < new Date(updated.registrationStartDate)) {
          updated.registrationEndDate = updated.registrationStartDate;
        }
      }

      setEditedTournament(updated);
      
      // Check if there are changes
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(tournament);
      setHasChanges(hasChanges);
    }
  };

  const handleAddTeam = () => {
    setEditingTeam(null);
    setShowAddTeamForm(true);
    
    // Scroll to the form after it's rendered
    setTimeout(() => {
      if (teamFormRef.current) {
        teamFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure form is rendered
  };

  const handleExportTeams = async () => {
    if (!tournament || !id || exporting) {
      return;
    }
    
    try {
      setExporting(true);
      
      // Call the export API
      const blob = await exportTournamentTeams(id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with tournament name and current date
      const currentDate = new Date().toISOString().split('T')[0];
      const tournamentName = tournament.name.replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `${tournamentName}_teams_${currentDate}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      // Show more specific error message
      const errorMessage = error.message || 'Failed to export teams. Please try again.';
      alert(errorMessage);
    } finally {
      setExporting(false);
    }
  };


  const handleEditTeam = async (team: Team) => {
    try {
      setIsLoadingTeamDetails(true);
      setTeamDetailsError(null);
      
      // Fetch complete team details using the specified API
      console.log('Fetching team details for team ID:', team._id);
      const teamDetails = await getTeamById(team._id);
      console.log('Team details fetched:', teamDetails);
      
      // Create a complete team object with all the details
      const completeTeam: Team = {
        ...team,
        coachManagerName: teamDetails.managerName || team.coachManagerName || '',
        contactDetails: teamDetails.managerContactNum?.toString() || team.contactDetails || '',
        // Map other fields from teamDetails if needed
        name: teamDetails.name || team.name,
        shortName: teamDetails.shortName || team.shortName,
        logo: teamDetails.logo || team.logo,
        ageGroups: Array.isArray(teamDetails.ageCategory) 
          ? teamDetails.ageCategory 
          : teamDetails.ageCategory 
            ? [teamDetails.ageCategory] 
            : team.ageGroups,
        // ✅ Fix: Map clubId from teamDetails.clubInfo._id
        clubId: teamDetails.clubInfo?._id || team.clubId || ''
      };
      
      setEditingTeam(completeTeam);
      setShowAddTeamForm(true);
      
      // Scroll to the form after it's rendered
      setTimeout(() => {
        if (teamFormRef.current) {
          teamFormRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100); // Small delay to ensure form is rendered
      
    } catch (error: any) {
      console.error('Error fetching team details:', error);
      setTeamDetailsError(error.message || 'Failed to load team details');
      
      // Fallback: use the basic team data if API fails
      setEditingTeam(team);
      setShowAddTeamForm(true);
      
      // Scroll to the form after it's rendered
      setTimeout(() => {
        if (teamFormRef.current) {
          teamFormRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    } finally {
      setIsLoadingTeamDetails(false);
    }
  };

  const handleCloseTeamForm = () => {
    setShowAddTeamForm(false);
    setEditingTeam(null);
    setTeamDetailsError(null);
  };

  const handleDeleteTeam = (teamId: string) => {
    const team = teams.find(t => t._id === teamId);
    if (team) {
      setTeamToDelete(team);
      setShowDeleteModal(true);
      setDeleteError(null);
    }
  };

  // Confirm delete team - actual deletion
  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      // Call the delete team API
      await deleteTeam(teamToDelete._id);
      
      // Remove team from local state
      setTeams(teams.filter(team => team._id !== teamToDelete._id));
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setTeamToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete team:', err);
      setDeleteError(err.message || 'Failed to delete team. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTeamToDelete(null);
    setDeleteError(null);
  };

  const handleAddPlayer = (team: Team) => {
    console.log('Add player for team:', team.name);
    navigate(`/registered-tournaments/${id}/teams/${team._id}/players?mode=add`, {
      state: {
        team,
        tournament,
        source: 'add',
        isRecordPayment: true,
        paymentMode: 'offline'
      }
    });
  };

  const handleManagePlayers = (team: Team) => {
    console.log('Manage players for team:', team.name);
    navigate(`/registered-tournaments/${id}/teams/${team._id}/players?mode=manage`, {
      state: {
        team,
        tournament,
        source: 'manage',
        isRecordPayment: true,
        paymentMode: 'offline'
      }
    });
  };

  // REMOVED: Old getUnpaidPlayerIds function - replaced by validateAndFixUnpaidPlayerIds

  // Helper function to get all player IDs from team data
  const getAllPlayerIds = async (team: Team): Promise<string[]> => {
    try {
      console.log('🔍 getAllPlayerIds - Getting players for team:', team._id, 'tournament:', id);
      // Get team players using the correct service function
      const players = await getPlayersByTeamId(team._id, id); // Pass tournament ID as well
      console.log('🔍 getAllPlayerIds - Raw players response:', players);
      const playerIds = players.map((player) => player._id);
      console.log('🔍 getAllPlayerIds - Extracted player IDs:', playerIds);
      return playerIds;
    } catch (error) {
      console.error('🚨 getAllPlayerIds - Error getting all player IDs:', error);
      return [];
    }
  };

  // ✅ GLOBAL VALIDATION FUNCTION: Ensures unpaid player IDs are correct
  const validateAndFixUnpaidPlayerIds = async (team: Team): Promise<string[]> => {
    try {
      console.log('🔍 GLOBAL VALIDATION (authoritative): Validating unpaid player IDs for team:', team.name);

      // Always fetch fresh players from API (single source of truth)
      const players = await getPlayersByTeamId(team._id, id);
      const allPlayerIds = players.map(p => p._id);

      // Derive paid/unpaid from player details
      const paidFromDetails = players
        .filter(p => (p as any).paymentStat === 'PAID' || (((p as any).amountDue ?? null) !== null && ((p as any).amountDue) <= 0))
        .map(p => p._id);

      const unpaidFromDetails = players
        .filter(p => !paidFromDetails.includes(p._id))
        .map(p => p._id);

      // Sanity comparison with team-provided arrays
      if (team.paidPlayerIds && team.paidPlayerIds.length > 0) {
        const diffPaid = team.paidPlayerIds.filter(pid => !paidFromDetails.includes(pid));
        if (diffPaid.length > 0) {
          console.warn('🔍 Sanity check: team.paidPlayerIds not fully aligned with player details. Diff:', diffPaid);
        }
      }

      console.log('🔍 Authoritative unpaid player IDs:', unpaidFromDetails);

      // ✅ FINAL VALIDATION: Ensure no paid players are in the result
      const hasPaidLeak = unpaidFromDetails.some(pid => paidFromDetails.includes(pid));
      if (hasPaidLeak) {
        console.error('🔍 CRITICAL: Paid IDs leaked into unpaid result. Forcing filter.');
        const forced = allPlayerIds.filter(pid => !paidFromDetails.includes(pid));
        console.log('🔍 Forced correct unpaid:', forced);
        return forced;
      }

      return unpaidFromDetails;
    } catch (error) {
      console.error('🔍 Error in validateAndFixUnpaidPlayerIds (authoritative) for team:', team.name, error);
      throw error;
    }
  };

  const handleRecordPaymentWithPreview = async (team: Team) => {
    console.log('🔍 Record payment with preview for team:', team.name);
    
    // Prevent multiple simultaneous calls for the same team
    if (recordPaymentLoadingTeamId === team._id) {
      console.log('🔍 Record payment already in progress for this team, ignoring duplicate call');
      return;
    }
    
    setIsRecordPaymentLoading(true);
    setRecordPaymentLoadingTeamId(team._id);
    setRecordPaymentError(null);
    
    // Add a safety timeout to reset loading state if something goes wrong
    const safetyTimeout = setTimeout(() => {
      console.log('🚨 SAFETY TIMEOUT: Resetting loading state after 30 seconds');
      setIsRecordPaymentLoading(false);
      setRecordPaymentLoadingTeamId(null);
      setRecordPaymentError('Request timed out. Please try again.');
    }, 30000); // 30 seconds timeout
    
    try {
      // Get all players for the team (no selection needed)
      let allPlayerIds = await getAllPlayerIds(team);
      
      // Fallback: Use team.playerIds if API call fails or returns empty
      if (allPlayerIds.length === 0 && team.playerIds && team.playerIds.length > 0) {
        console.log('🔍 No players from API, using team.playerIds as fallback:', team.playerIds);
        allPlayerIds = team.playerIds;
      }
      
      if (allPlayerIds.length === 0) {
        throw new Error('No players found for this team. Please add players first.');
      }
      
      console.log('🔍 All player IDs for payment:', allPlayerIds);
      
      // ✅ UNIFIED API LOGIC: Always use the same API endpoint
      let paymentPreview;
      
      const ageCategory = team.ageGroups?.[0] || 'U10';
      console.log('🔍 Using unified payment-preview API');
      console.log('🔍 Tournament ID:', id);
      console.log('🔍 Team ID:', team._id);
      console.log('🔍 Registration ID:', team.registration || null);
      
      // Add timeout protection for the API call
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Payment preview request timed out after 30 seconds')), 30000);
      });
      
      const apiCall = getPaymentPreview(
        id!, // tournamentId
        team._id, // teamId
        {
          registrationId: team.registration || null, // Always send registrationId (null or actual ID)
          ageCategory,
          players: allPlayerIds
        }
      );
      
      paymentPreview = await Promise.race([apiCall, timeoutPromise]);
      
      console.log('🔍 Payment preview response:', paymentPreview);
      
      // Navigate to payment overview screen
      const navigationState = {
          team,
          tournament,
        allPlayers: allPlayerIds,
          paymentPreview: paymentPreview.data,
        hasExistingRegistration: !!team.registration,
        registrationId: team.registration || null,
          isRecordPayment: true,
          paymentMode: 'offline'
      };
      
      console.log('🔍 Navigating to payment page with state');
      
      navigate(`/registered-tournaments/${id}/teams/${team._id}/record-payment`, {
        state: navigationState
      });
      
    } catch (error: any) {
      console.error('Error in record payment flow:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to initiate record payment';
      
      if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your internet connection and try again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Payment service not found. Please contact support.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setRecordPaymentError(errorMessage);
      
      // Fallback: Navigate to payment page with basic data if API fails
      if (error.message?.includes('timeout') || error.response?.status >= 500) {
        console.log('🔍 API failed, attempting fallback navigation...');
        try {
          navigate(`/registered-tournaments/${id}/teams/${team._id}/record-payment`, {
            state: {
              team,
              tournament,
              allPlayers: team.playerIds || [],
              hasExistingRegistration: !!team.registration,
              registrationId: team.registration || null,
              isRecordPayment: true,
              paymentMode: 'offline',
              fallbackMode: true // Flag to indicate this is a fallback
            }
          });
        } catch (navError) {
          console.error('🚨 Fallback navigation also failed:', navError);
        }
      }
    } finally {
      clearTimeout(safetyTimeout); // Clear the safety timeout
      setIsRecordPaymentLoading(false);
      setRecordPaymentLoadingTeamId(null); // Reset the loading team ID
    }
  };

  const handlePayNow = (team: Team) => {
    console.log('🔍 handlePayNow called for team:', team.name);
    
    try {
      handleRecordPaymentWithPreview(team);
    } catch (error) {
      console.error('🚨 Error in handlePayNow:', error);
      setRecordPaymentError('Failed to initiate payment process');
      setIsRecordPaymentLoading(false);
      setRecordPaymentLoadingTeamId(null);
    }
  };

  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || isPageLoading) {
      return;
    }
    
    setCurrentPage(page);
    
    // Scroll to top for better UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Load teams for the new page directly
    await loadTeams(page);
    
  };

  const handleSaveTeam = async (teamData: CreateTeamRequest & { clubId: string }) => {
    try {
      console.log('Saving team:', teamData);
      
      if (editingTeam) {
        // ✅ Fix: Implement team update logic
        console.log('Updating team:', editingTeam._id, teamData);
        
        // Prepare update data (include club ID in update request and convert ageGroups to ageCategory)
        const updateData = {
          name: teamData.name,
          shortName: teamData.shortName,
          logo: teamData.logo,
          ageCategory: teamData.ageCategory && teamData.ageCategory.length > 0 ? teamData.ageCategory[0] : '', // Convert array to string
          managerName: teamData.managerName, // Use managerName from form data
          managerContactNum: teamData.managerContactNum, // Use managerContactNum from form data
          location: teamData.location || '', // Add location field
          countryCode: teamData.countryCode || 91, // Add countryCode field with default value
          club: teamData.clubId // Include club ID in update payload
        };
        
        const response = await updateTeam(editingTeam._id, updateData);
        
        if (response.success) {
          console.log('Team updated successfully:', response);
          setShowAddTeamForm(false);
          setEditingTeam(null);
          // Reload teams to show the updated team
          loadTeams(currentPage, searchTerm, ageGroupFilter);
        } else {
          throw new Error(response.message || 'Failed to update team');
        }
        return;
      }
      
      // Create new team using the API
      // Extract clubId and create clean API data
      const { clubId, ...apiTeamData } = teamData;
      
      if (!clubId) {
        throw new Error('Club ID is required');
      }
      
      // Add tournament ID to the API data for registered tournament context
      const teamDataWithTournament = {
        ...apiTeamData,
        tournament: id // Use the tournament ID from URL params
      };
      
      const response = await createTeam(clubId, teamDataWithTournament);
      
      if (response.success) {
        console.log('Team created successfully:', response);
        setShowAddTeamForm(false);
        setEditingTeam(null);
        // Reload teams to show the new team
        loadTeams(currentPage, searchTerm, ageGroupFilter);
      } else {
        throw new Error(response.message || 'Failed to create team');
      }
    } catch (error: any) {
      console.error('🟡 Error saving team in handleSaveTeam:', error);
      console.error('🟡 Error message in handleSaveTeam:', error.message);
      console.error('🟡 Error type in handleSaveTeam:', typeof error);
      console.error('🟡 Error stack in handleSaveTeam:', error.stack);
      // Error will be handled by the TeamForm component
      throw error;
    }
  };

  // Remove the duplicate filtering logic since TeamsTable handles it internally
  // const filteredTeams = teams.filter(team => {
  //   const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //                        team.shortName.toLowerCase().includes(searchTerm.toLowerCase());
  //   const matchesAgeGroup = ageGroupFilter === 'All' || 
  //                          team.ageGroups.includes(ageGroupFilter);
  //   return matchesSearch && matchesAgeGroup;
  // });

  if (loading) {
    return <ShimmerLoader type="table" />;
  }

  if (!tournament) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <h4>Tournament not found</h4>
          <Button onClick={() => navigate('/registered-tournaments')}>
            Back to Tournaments
          </Button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Tournaments', onClick: () => navigate('/tournaments') },
    { label: 'Tournament Team Registrations', onClick: () => navigate('/registered-tournaments') },
    { label: 'Registered Tournament Detail', active: true }
  ];

  return (
    <div className="page-content registered-tournament-detail">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        {/* Page Header with Back Button and Tournament Logo */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
            <button 
              className="back-button"
              onClick={() => navigate('/registered-tournaments')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            {tournament?.logo && (
              <div className="tournament-logo-small" style={{ flexShrink: 0 }}>
                <img 
                  src={tournament.logo} 
                  alt={tournament?.name || 'Tournament Logo'}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <h1 className="page-title mb-0">{tournament?.name || 'Tournament'}</h1>
            </div>
          </div>
        </div>

        <Row>
          <Col>
            {/* Tournament Details Section */}
            <Card className={`tournament-form-card mb-4 ${!isTournamentDetailsExpanded ? 'collapsed-card' : ''}`}>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-link p-0 me-2"
                      onClick={() => setIsTournamentDetailsExpanded(!isTournamentDetailsExpanded)}
                      style={{ border: 'none', background: 'none', color: '#6c757d' }}
                    >
                      <i className={`ri-arrow-${isTournamentDetailsExpanded ? 'up' : 'down'}-s-line`}></i>
                    </button>
                    <h5 className="card-title mb-0">
                      <i className="ri-trophy-line me-2"></i>
                      Tournament Details
                    </h5>
                  </div>
                  <div className="d-flex gap-2">
                    {isEditMode ? (
                      <>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          <i className="ri-close-line me-1"></i>
                          Cancel
                        </Button>
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={handleSaveTournament}
                          disabled={!hasChanges}
                        >
                          <i className="ri-save-line me-1"></i>
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={handleToggleEditMode}
                      >
                        <i className="ri-pencil-line me-1"></i>
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className={isTournamentDetailsExpanded ? '' : 'd-none'}>
                {/* Tournament Details Fields - Clean Layout */}
                <div className="tournament-fields-container">
                  {/* First Row: Tournament Logo and Basic Info */}
                  <div className="field-row logo-row">
                    <div className="field-group">
                      <label>Tournament Logo</label>
                      <div className="tournament-logo-preview">
                        <img 
                          src={(isEditMode ? editedTournament?.logo : tournament.logo) || '/default-tournament-logo.png'} 
                          alt="Tournament Logo"
                          className="tournament-logo-image"
                        />
                      </div>
                    </div>

                    <div className="field-group">
                      <label>Tournament Name</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={editedTournament?.name || ''}
                          onChange={(e) => handleTournamentFieldChange('name', e.target.value)}
                          placeholder="Enter tournament name"
                        />
                      ) : (
                        <div className="field-value">{tournament.name || 'N/A'}</div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Short Name</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={editedTournament?.shortName || ''}
                          onChange={(e) => handleTournamentFieldChange('shortName', e.target.value)}
                          placeholder="Enter short name"
                        />
                      ) : (
                        <div className="field-value">{tournament.shortName || 'N/A'}</div>
                      )}
                    </div>
                  </div>

                  {/* Second Row: Sports and Venue */}
                  <div className="field-row two-column">
                    <div className="field-group">
                      <label>Sports</label>
                      {isEditMode ? (
                        <Select
                          classNamePrefix="react-select"
                          value={sportsOptions.find(option => option.value === editedTournament?.sports)}
                          onChange={(selectedOption) => handleTournamentFieldChange('sports', selectedOption?.value || '')}
                          options={sportsOptions}
                          placeholder="Select sports"
                          isClearable={false}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                      ) : (
                        <div className="field-value">
                          <Badge bg="success">{tournament.sports || 'N/A'}</Badge>
                        </div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Venue</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={editedTournament?.location || ''}
                          onChange={(e) => handleTournamentFieldChange('location', e.target.value)}
                          placeholder="Enter venue"
                        />
                      ) : (
                        <div className="field-value">{tournament.location || 'N/A'}</div>
                      )}
                    </div>
                  </div>

                  {/* Third Row: Tournament Dates */}
                  <div className="field-row two-column">
                    <div className="field-group">
                      <label>Start Date</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={editedTournament?.startDate || ''}
                          onChange={(e) => handleTournamentFieldChange('startDate', e.target.value)}
                        />
                      ) : (
                        <div className="field-value">{formatDate(tournament.startDate)}</div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>End Date</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={editedTournament?.endDate || ''}
                          onChange={(e) => handleTournamentFieldChange('endDate', e.target.value)}
                          min={editedTournament?.startDate || tournament.startDate}
                        />
                      ) : (
                        <div className="field-value">{formatDate(tournament.endDate)}</div>
                      )}
                    </div>
                  </div>

                  {/* Fourth Row: Registration Dates */}
                  <div className="field-row two-column">
                    <div className="field-group">
                      <label>Registration Start Date</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={editedTournament?.registrationStartDate || ''}
                          onChange={(e) => handleTournamentFieldChange('registrationStartDate', e.target.value)}
                        />
                      ) : (
                        <div className="field-value">
                          {tournament.registrationStartDate ? formatDate(tournament.registrationStartDate) : 'Not set'}
                        </div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Registration End Date</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={editedTournament?.registrationEndDate || ''}
                          onChange={(e) => handleTournamentFieldChange('registrationEndDate', e.target.value)}
                          min={editedTournament?.registrationStartDate || tournament.registrationStartDate}
                        />
                      ) : (
                        <div className="field-value">
                          {tournament.registrationEndDate ? formatDate(tournament.registrationEndDate) : 'Not set'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fifth Row: Age Categories */}
                  <div className="field-row single-column">
                    <div className="field-group">
                      <label>Age Categories</label>
                      {isEditMode ? (
                        <Select
                          isMulti
                          classNamePrefix="react-select"
                          value={editedTournament?.ageCategories?.map(cat => ({ value: cat, label: cat })) || []}
                          onChange={(selectedOptions) => {
                            const categories = selectedOptions?.map(option => option.value) || [];
                            handleTournamentFieldChange('ageCategories', categories);
                          }}
                          options={ageCategories.map(cat => ({ value: cat, label: cat }))}
                          placeholder="Select age categories"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          maxMenuHeight={400}
                          styles={{ 
                            menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                            menu: (base: any) => ({ 
                              ...base, 
                              maxHeight: 400,
                              overflow: 'hidden'
                            }),
                            menuList: (base: any) => ({
                              ...base,
                              maxHeight: 400,
                              padding: 0,
                              overflowY: 'auto',
                              overflowX: 'hidden'
                            })
                          }}
                        />
                      ) : (
                        <div className="field-value">
                          {tournament.ageCategories && tournament.ageCategories.length > 0 ? (
                            <div className="age-category-tags">
                              {tournament.ageCategories.map((age, index) => (
                                <span key={index} className="age-category-tag">
                                  {age}
                                </span>
                              ))}
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Registered Teams Section */}
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 me-4">
                      <i className="ri-team-line me-2"></i>
                      Registered Teams
                    </h5>
                    {/* Search and Filters inline with title */}
                    <div className="d-flex align-items-center gap-3">
                      <div className="search-input-wrapper" style={{ minWidth: '250px' }}>
                        <input
                          type="text"
                          className="form-search"
                          placeholder="Search teams..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ width: '100%', paddingRight: '35px' }}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="clear-search-btn"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                      </div>
                      <Select
                        value={ageCategoryOptions.find(option => option.value === ageGroupFilter)}
                        onChange={(selectedOption) => setAgeGroupFilter(selectedOption?.value || 'all')}
                        options={ageCategoryOptions}
                        placeholder="Select Age Category"
                        isClearable={false}
                        styles={{
                          container: (base) => ({ ...base, minWidth: '180px' }),
                          control: (base) => ({ ...base, minHeight: '38px' })
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="success" 
                      onClick={handleExportTeams}
                      disabled={exporting}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" style={{ width: '16px', height: '16px' }} />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <i className="ri-download-line"></i>
                          Export Teams
                        </>
                      )}
                    </Button>
                    <Button variant="primary" onClick={handleAddTeam}>
                      Add New Team
                      <i className="ri-add-line ms-2"></i>
                    </Button>
                  </div>
                </div>
              </Card.Header>
              
              <Card.Body>

                {/* Add/Edit Team Form Card */}
                {showAddTeamForm && (
                  <Card className="team-form-card mb-4" ref={teamFormRef}>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">{editingTeam ? 'Edit Team Details' : 'Add Team Details'}</h5>
                        <Button 
                          variant="link" 
                          className="close-form-btn"
                          onClick={handleCloseTeamForm}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </div>
                      {isLoadingTeamDetails ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading team details...</span>
                          </Spinner>
                          <p className="mt-2">Loading team details...</p>
                        </div>
                      ) : (
                        <>
                          {teamDetailsError && (
                            <div className="alert alert-warning mb-3" role="alert">
                              <i className="ri-error-warning-line me-2"></i>
                              {teamDetailsError}
                            </div>
                          )}
                          <TeamForm 
                            team={editingTeam}
                            ageCategories={tournament.ageCategories}
                            onSave={handleSaveTeam}
                            onCancel={handleCloseTeamForm}
                            tournamentId={id || ''}
                          />
                        </>
                      )}
                    </CardBody>
                  </Card>
                )}

                                {/* Teams Table */}
                {teamsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading teams...</span>
                    </Spinner>
                    <p className="mt-2">Loading teams...</p>
                  </div>
                ) : (
                  <div className="tournament-team-registrations-table-container">
                    <TeamsTable
                      teams={teams}
                      loading={teamsLoading}
                      showCheckboxes={false}
                      singleSelection={false}
                      showAddPlayers={true}
                      showManagePlayers={true}
                      showViewDetails={false}
                      showEdit={true}
                      showDelete={true}
                      showPayNow={true}
                      showPaymentStats={true}
                      isRecordPayment={true}
                      isRecordPaymentLoading={isRecordPaymentLoading}
                      recordPaymentLoadingTeamId={recordPaymentLoadingTeamId}
                      onAddPlayers={(teamId) => {
                        const team = teams.find(t => t._id === teamId);
                        if (team) handleAddPlayer(team);
                      }}
                      onManagePlayers={(teamId) => {
                        const team = teams.find(t => t._id === teamId);
                        if (team) handleManagePlayers(team);
                      }}
                      onEdit={(team) => handleEditTeam(team)}
                      onDelete={(teamId) => handleDeleteTeam(teamId)}
                      onPayNow={(team) => handlePayNow(team)}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      ageGroupFilter={ageGroupFilter}
                      onAgeGroupFilterChange={setAgeGroupFilter}
                      showFilters={false}
                      showSearch={false}
                      title="Registered Teams"
                      emptyMessage="No teams registered yet"
                      emptySubMessage="No teams available for this tournament."
                      ageCategories={tournament?.ageCategories || []}
                      conditionalActionVisibility={true}
                    />

                    {/* Record Payment Error Display */}
                    {recordPaymentError && (
                      <div className="alert alert-danger mt-3" role="alert">
                        <i className="ri-error-warning-line me-2"></i>
                        {recordPaymentError}
                        <button 
                          className="btn btn-sm btn-outline-secondary ms-2"
                          onClick={() => setRecordPaymentError(null)}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}


                    {/* Pagination Controls */}
                    {(() => {
                      const shouldShowPagination = totalPages > 1;
                      return shouldShowPagination ? (
                        <div className="pagination-section mt-4" style={{ 
                          padding: '16px 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {/* Page Info */}
                          <div className="page-info mb-3">
                            <span className="text-muted" style={{ 
                              fontSize: '14px', 
                              fontWeight: '500',
                              color: '#6c757d'
                            }}>
                              Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalTeams)} of {totalTeams} teams
                            </span>
                          </div>

                          {/* Pagination Controls */}
                          <div className="pagination-controls d-flex align-items-center justify-content-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1 || isPageLoading}
                              style={{ 
                                minWidth: '90px', 
                                height: '36px',
                                fontSize: '13px',
                                borderColor: currentPage === 1 ? '#e9ecef' : '#ced4da',
                                backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                                color: currentPage === 1 ? '#adb5bd' : '#495057',
                                borderRadius: '6px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <i className="ri-arrow-left-s-line me-1"></i>
                              Previous
                            </Button>
                            
                            <div className="d-flex align-items-center mx-3">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }

                                return (
                                  <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? "primary" : "outline-secondary"}
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    disabled={isPageLoading}
                                    style={{ 
                                      minWidth: '36px', 
                                      height: '36px',
                                      fontSize: '13px',
                                      margin: '0 2px',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                            </div>
                            
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages || isPageLoading}
                              style={{ 
                                minWidth: '90px', 
                                height: '36px',
                                fontSize: '13px',
                                borderColor: currentPage === totalPages ? '#e9ecef' : '#ced4da',
                                backgroundColor: currentPage === totalPages ? '#f8f9fa' : '#ffffff',
                                color: currentPage === totalPages ? '#adb5bd' : '#495057',
                                borderRadius: '6px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Next
                              <i className="ri-arrow-right-s-line ms-1"></i>
                            </Button>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Delete Team Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${teamToDelete?.name}"? This action cannot be undone and will remove all associated players and data.`}
        confirmLabel="Delete Team"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />

    </div>
  );
};

// Team Form Component
interface TeamFormProps {
  team: Team | null;
  ageCategories: string[];
  onSave: (teamData: CreateTeamRequest & { clubId: string }) => void;
  onCancel: () => void;
  tournamentId: string;
}

const TeamForm: React.FC<TeamFormProps> = ({ team, ageCategories, onSave, onCancel, tournamentId }) => {
  // Form state
  const [formData, setFormData] = useState({
    clubId: team?.clubId || '', // Fix: Initialize clubId from team data
    name: team?.name || '',
    shortName: team?.shortName || '',
    logo: team?.logo || '',
    ageGroups: team?.ageGroups || [],
    coachManagerName: team?.coachManagerName || '',
    contactDetails: team?.contactDetails || ''
  });

  // Club selection state
  const [clubs, setClubs] = useState<ClubListingData[]>([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  // Image upload state
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Editing restrictions based on player count
  const isClubDisabled = Boolean(team && team.players > 0);
  const isAgeCategoryDisabled = Boolean(team && team.players > 0);

  // Load clubs on component mount
  useEffect(() => {
    const loadClubs = async () => {
      try {
        setClubsLoading(true);
        const clubsData = await getAllClubs();
        console.log('🏢 Loaded clubs data:', clubsData);
        console.log('🏢 Club IDs:', clubsData.map(c => ({ id: c._id, name: c.name })));
        setClubs(clubsData);
      } catch (error) {
        console.error('Failed to load clubs:', error);
      } finally {
        setClubsLoading(false);
      }
    };

    loadClubs();
  }, []);

  // Update form data when team prop changes (for edit mode)
  useEffect(() => {
    if (team) {
      console.log('🔄 Updating form data for team:', team);
      console.log('🏢 Available clubs:', clubs.length, clubs.map(c => ({ id: c._id, name: c.name })));
      console.log('🎯 Team clubId:', team.clubId);
      console.log('🔍 Matching club:', clubs.find(club => club._id === team.clubId));
      
      setFormData({
        clubId: team.clubId || '',
        name: team.name || '',
        shortName: team.shortName || '',
        logo: team.logo || '',
        ageGroups: team.ageGroups || [],
        coachManagerName: team.coachManagerName || '',
        contactDetails: team.contactDetails || ''
      });
    } else {
      // Reset form data for add mode
      console.log('🔄 Resetting form data for add mode');
      setFormData({
        clubId: '',
        name: '',
        shortName: '',
        logo: '',
        ageGroups: [],
        coachManagerName: '',
        contactDetails: ''
      });
    }
  }, [team, clubs]); // Add clubs to dependencies to re-run when clubs are loaded

  // Validation functions
  const validateContactNumber = (contact: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(contact.replace(/\D/g, ''));
  };

  const isFormValid = (): boolean => {
    return (
      formData.clubId.trim() !== '' &&
      formData.name.trim() !== '' &&
      formData.shortName.trim() !== '' &&
      formData.ageGroups.length > 0 &&
      formData.contactDetails.trim() !== '' &&
      validateContactNumber(formData.contactDetails)
    );
  };

  // Handle form field changes
  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError(null); // Clear submit error when user makes changes
  };

  // Handle logo upload
  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input
    e.target.value = '';
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    try {
      setIsLogoUploading(true);
      setLogoError(null);
      setLogoUploadProgress(0);
      setLogoUploadSuccess(false);

      // Compress image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setLogoUploadProgress(progress)
      });

      // Generate file name and context (same as All Clubs)
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `team_logo_${Date.now()}.${ext}`;
      const context = `my-club/team-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;

      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoUploadSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setLogoUploadSuccess(false), 3000);
      
    } catch (error) {
      console.error('Logo upload error:', error);
      setLogoError('Failed to upload logo. Please try again.');
    } finally {
      setIsLogoUploading(false);
      setLogoUploadProgress(0);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setSubmitError('Please fill all required fields correctly');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const teamData: CreateTeamRequest & { clubId: string } = {
        name: formData.name,
        shortName: formData.shortName,
        logo: formData.logo,
        managerName: formData.coachManagerName,
        countryCode: 91, // Default to India
        managerContactNum: parseInt(formData.contactDetails.replace(/\D/g, '')) || 0,
        location: 'Bengaluru', // Default location
        ageCategory: formData.ageGroups,
        tournament: tournamentId, // Include tournament ID for registered tournament context
        clubId: formData.clubId // Include the selected club ID
      };

      await onSave(teamData);
    } catch (error: any) {
      console.error('🔴 Error saving team in TeamForm:', error);
      console.error('🔴 Error message in TeamForm:', error.message);
      console.error('🔴 Error type in TeamForm:', typeof error);
      console.error('🔴 Error stack in TeamForm:', error.stack);
      
      const errorMessage = error.message || 'Failed to save team. Please try again.';
      console.log('🔴 Setting submit error to:', errorMessage);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Club Selection */}
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Club<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
            <Select
              classNamePrefix="react-select"
              value={clubs.find(club => club._id === formData.clubId) ? { value: formData.clubId, label: clubs.find(club => club._id === formData.clubId)?.name } : null}
              onChange={(selectedOption) => {
                handleFormChange('clubId', selectedOption?.value || '');
              }}
              options={clubs.map(club => ({ value: club._id, label: club.name }))}
              placeholder="Select a club"
              isLoading={clubsLoading}
              isDisabled={clubsLoading || isClubDisabled}
              styles={{
                control: (base) => ({ 
                  ...base, 
                  minHeight: '45px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                })
              }}
            />
            {isClubDisabled && (
              <div className="form-text text-muted mt-1">
                <i className="ri-information-line me-1"></i>
                Club cannot be changed once players are registered to the team
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Team Logo Upload */}
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Team Logo</Form.Label>
            <div className="team-logo-upload-section">
              <div className="team-logo-upload">
                <div 
                  className="team-logo-preview"
                  style={{
                    background: formData.logo ? `url(${getImageUrl(formData.logo)}) center/cover` : '#f8f9fa'
                  }}
                  onClick={handleLogoClick}
                >
                  {!formData.logo && (
                    <div className="upload-placeholder">
                      <i className="ri-team-line"></i>
                      <div className="small">Upload Team Logo</div>
                    </div>
                  )}
                  {isLogoUploading && (
                    <div className="upload-overlay">
                      <div className="spinner-border spinner-border-sm text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="small mt-2 text-light">Uploading... {Math.round(logoUploadProgress)}%</div>
                    </div>
                  )}
                </div>
                {formData.logo && (
                  <div 
                    className="edit-icon"
                    onClick={handleLogoClick}
                  >
                    <i className="ri-pencil-line"></i>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  style={{ display: 'none' }}
                />
              </div>
              
              {/* Logo upload error and success */}
              {logoError && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  {logoError}
                </div>
              )}
              
              {logoUploadSuccess && (
                <div className="document-upload-success">
                  <i className="ri-check-line"></i>
                  Team logo uploaded successfully!
                </div>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>
      
      {/* Team Details */}
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Team Name<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="Enter team name"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Abbreviation<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.shortName}
              onChange={(e) => {
                // Only allow alphabets and limit to 6 characters
                const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                handleFormChange('shortName', value);
              }}
              placeholder="Enter abbreviation (alphabets only, max 6 chars)"
              maxLength={6}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Age Category<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
            <Select
              classNamePrefix="react-select"
              value={formData.ageGroups && formData.ageGroups.length > 0 ? { value: formData.ageGroups[0], label: formData.ageGroups[0] } : null}
              onChange={(selectedOption) => {
                const selectedValue = selectedOption ? [selectedOption.value] : [];
                handleFormChange('ageGroups', selectedValue);
              }}
              options={ageCategories.map(age => ({ value: age, label: age }))}
              placeholder="Select age category"
              isDisabled={isAgeCategoryDisabled}
              maxMenuHeight={400}
              menuPosition="fixed"
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ 
                  ...base, 
                  minHeight: '45px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }),
                                 menu: (base) => ({ 
                   ...base, 
                   zIndex: 99999,
                   maxHeight: 400,
                   overflow: 'hidden'
                 }),
                 menuPortal: (base) => ({ 
                   ...base, 
                   zIndex: 99999 
                 }),
                menuList: (base) => ({
                  ...base,
                  maxHeight: 400,
                  padding: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                })
              }}
            />
            {isAgeCategoryDisabled && (
              <div className="form-text text-muted mt-1">
                <i className="ri-information-line me-1"></i>
                Age category cannot be changed once players are registered to the team
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Contact Details */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Coach/Manager Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.coachManagerName}
              onChange={(e) => handleFormChange('coachManagerName', e.target.value)}
              placeholder="Enter coach/manager name"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Contact Details<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
            <Form.Control
              type="tel"
              value={formData.contactDetails}
              onChange={(e) => handleFormChange('contactDetails', e.target.value)}
              placeholder="Enter contact number"
              isInvalid={formData.contactDetails.trim() !== '' && !validateContactNumber(formData.contactDetails)}
            />
            {formData.contactDetails.trim() !== '' && !validateContactNumber(formData.contactDetails) && (
              <Form.Control.Feedback type="invalid">
                Please enter a valid 10-digit mobile number
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Submit Error */}
      {submitError && (
        <div className="alert alert-danger" role="alert">
          <i className="ri-error-warning-line me-2"></i>
          {submitError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          type="submit" 
          disabled={!isFormValid() || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {team ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            team ? 'Update Team' : 'Add Team'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RegisteredTournamentDetail; 